/**
 * 统一组合草图实体的偏移方向（等距实体）。
 *
 * 职责说明：
 * 1. 该文件的唯一作用就是统一组合草图实体（多条实体组成的选择链）的偏移方向，
 *    即为链中每个实体计算逐实体 isReverse（最终生效值 = baseline XOR globalIsReverse）；
 * 2. 全部方法均为静态纯函数，不依赖任何实例状态 / Viewer，
 *    通过显式参数（refPlane / globalIsReverse）注入外部依赖，供 SketchOffsetController 等外部调用；
 * 3. 组合草图实体只分为两种情况：
 *    - 闭合链：射线法（Point-in-Polygon）逐实体独立判定，全部朝轮廓同侧偏移；
 *      判定轮廓与测试点均基于各实体 geometry 的真实采样点（圆弧/椭圆弧/抛物线等
 *      曲线不再用端点弦近似，避免弓形歧义区与半圆弧"弦中点=圆心"退化）；
 *    - 开口链：统一使用"行进方向修正"——以各实体自身 start→end 方向的默认偏移侧为基准，
 *      用链连接拓扑确定的统一行进方向与各实体自身方向比较，反向则翻转该实体的 isReverse。
 *      圆弧/椭圆弧均有明确首尾端点，且默认偏移方向与线段一致，同样适用该算法；
 *      抛物线的默认偏移侧由射线法决定（可能落在弦顺时针法向的异侧），
 *      改为直接比较相连端的实际默认偏移法向（见 applyChainDirectionCorrection）。
 */

import { Vector2, Vector3 } from 'three';
import type { ReferencePlane } from '../../reference';
import { readGeometryPositionPoints } from '../../utils';
import {
    AbstractSketchEdgeObject,
    SketchLineObject,
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchParabolaObject,
    SketchSplineObject,
    SketchStyleSplineObject,
    SketchBezierCurveClosureKind,
} from '../../mesh';
import type { SketchPointObject } from '../../mesh';
import type { OffsetCandidate, SketchEntityChain } from './type';
import { SketchEntityEquidistantOffset } from './SketchEntityEquidistantOffset';

export class CombineSketchEntitiesUnifiedDirection {

    // ═══════════════════════════════════════════════════════════
    // 拓扑支撑：端点 / 共享端点 / 多边形判定
    // ═══════════════════════════════════════════════════════════

    /**
     * 获取两个相邻实体在排序链中的共享端点世界坐标。
     * 
     * 说明：基于端点对象 uuid 匹配找到共享端点（委托 {@link getSharedEndpointObject}）。
     * 共享端点意味着两个实体在拓扑上通过同一个 SketchPointObject 相连。
     * 
     * @param a 实体 A。
     * @param b 实体 B。
     * @returns 共享端点的世界坐标，若无共享端点返回 null。
     */
    private static getSharedEndpoint(a: OffsetCandidate, b: OffsetCandidate): Vector3 | null {
        const shared = CombineSketchEntitiesUnifiedDirection.getSharedEndpointObject(a, b);
        return shared ? shared.getWorldPosition(new Vector3()) : null;
    }

    /**
     * 获取实体的两个拓扑端点对象（startPoint / endPoint）。
     * 
     * 说明：
     * 1. 直线/样条/样式样条直接暴露 startPoint、endPoint 公共属性；
     * 2. 圆弧/椭圆弧/抛物线通过 getStartPoint()/getEndPoint() 获取；
     * 3. 圆/非边实体没有拓扑端点，返回 { start: null, end: null }。
     * 
     * @param entity 草图实体。
     * @returns start/end 端点对象（可能为 null）。
     */
    private static getEntityEndpointObjects(entity: OffsetCandidate): {
        start: SketchPointObject | null;
        end: SketchPointObject | null;
    } {
        if (entity instanceof SketchLineObject
            || entity instanceof SketchSplineObject
            || entity instanceof SketchStyleSplineObject) {
            return { start: entity.startPoint, end: entity.endPoint };
        }
        if (entity instanceof SketchArcObject
            || entity instanceof SketchEllipseObject
            || entity instanceof SketchParabolaObject) {
            return { start: entity.getStartPoint(), end: entity.getEndPoint() };
        }
        return { start: null, end: null };
    }

    /**
     * 获取两个相邻实体在排序链中的共享端点对象。
     * 
     * 说明：基于 SketchPointObject 的 uuid 匹配（与 {@link getSharedEndpoint} 同语义），
     * 但返回端点对象本身，供闭合链行走算法同时使用其 uuid 与位置，
     * 避免"geometry 采样坐标与端点逻辑位置存在漂移"导致的方向误判。
     * 
     * @param a 实体 A。
     * @param b 实体 B。
     * @returns 共享端点对象；无共享端点返回 null。
     */
    private static getSharedEndpointObject(a: OffsetCandidate, b: OffsetCandidate): SketchPointObject | null {
        const aEndpoints = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(a);
        const bEndpoints = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(b);
        const candidates = [aEndpoints.start, aEndpoints.end];
        for (const ap of candidates) {
            if (ap && (ap.uuid === bEndpoints.start?.uuid || ap.uuid === bEndpoints.end?.uuid)) {
                return ap;
            }
        }
        return null;
    }

    /**
     * 获取实体的"另一个端点"对象（非 sharedObj 的那个端点）。
     * 
     * 说明：按 uuid 排除共享端点对象，返回剩余端点对象；退化（无另一端点）时返回 sharedObj 本身。
     * 
     * @param entity 草图实体。
     * @param sharedObj 与相邻实体共享的端点对象。
     * @returns 另一个端点对象；退化时返回 sharedObj。
     */
    private static getOtherEndpointObject(entity: OffsetCandidate, sharedObj: SketchPointObject): SketchPointObject {
        const endpoints = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(entity);
        if (endpoints.start && endpoints.start.uuid !== sharedObj.uuid) {
            return endpoints.start;
        }
        if (endpoints.end && endpoints.end.uuid !== sharedObj.uuid) {
            return endpoints.end;
        }
        return sharedObj;
    }

    /**
     * 获取实体的"另一个端点"（非 sharedPt 的那个端点）。
     * 
     * 说明：用于计算链方向时，找到实体中不与相邻实体共享的那个端点。
     * 遍历实体的端点列表，返回与 sharedPt 距离大于 1e-10 的第一个端点位置。
     * 
     * @param entity 草图实体。
     * @param sharedPt 与相邻实体共享的端点（世界坐标）。
     * @returns 另一个端点的世界坐标；无法获取时返回 sharedPt 的副本（退化回退）。
     */
    private static getOtherEndpoint(entity: OffsetCandidate, sharedPt: Vector3): Vector3 {
        if (!(entity instanceof AbstractSketchEdgeObject)) {
            return sharedPt.clone();
        }
        const endpoints = entity.getRelatedSketchPointsSnapshot()
            .filter(p => p.isEndPoint);
        for (const ep of endpoints) {
            const pos = ep.getWorldPosition(new Vector3());
            if (pos.distanceToSquared(sharedPt) > 1e-10) {
                return pos;
            }
        }
        // 退路：所有端点都与 sharedPt 重合（退化），返回 sharedPt
        return sharedPt.clone();
    }

    /**
     * 判断选择链是否为闭合链。
     * 
     * 说明：
     * 1. 单实体链：实体本身闭合（圆 / 完整椭圆 / 闭合样条）则闭合；
     * 2. 多实体链（≥2）：链首尾实体共享端点即闭合；
     * 3. 双实体共享两个端点（形成闭环）也视为闭合。
     * 
     * 公开原因：SketchOffsetController 阶段二（延伸相交处理）的 closedFlags 也复用本判定。
     * 
     * @param chain 已排序的选择链。
     * @returns 闭合返回 true。
     */
    static isChainClosed(chain: SketchEntityChain): boolean {
        if (chain.length === 0) return false;

        // 单实体：检查实体本身是否闭合
        if (chain.length === 1) {
            const entity = chain[0];
            const entityType = entity.objectType;
            if (entityType === 'Circle') return true;
            if (entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
                return entity.getCurveMetadata().closureKind === SketchBezierCurveClosureKind.Closed;
            }
            if (entity instanceof SketchEllipseObject) {
                const def = entity.getEllipseDefinition();
                return Math.abs(def.endAngle - def.startAngle - 2 * Math.PI) < 1e-6;
            }
            return false;
        }

        // 多实体（length ≥ 2）：双实体需共享≥2个不同端点才闭合，≥3实体首尾共享端点即闭合
        if (chain.length === 2) {
            return CombineSketchEntitiesUnifiedDirection.countSharedEndpoints(chain[0], chain[1]) >= 2;
        }

        return CombineSketchEntitiesUnifiedDirection.getSharedEndpoint(chain[0], chain[chain.length - 1]) !== null;
    }

    /**
     * 开环链端点侧：表示实体 start / end 拓扑端点。
     * 说明：偏移链与源链同序同拓扑，自由端「侧」一致，可按侧在偏移链上取对应端点。
     */
    public static getOpenChainFreeEndpointSides(chain: SketchEntityChain): {
        /** 链首实体的自由端侧（与相邻实体共享的另一端为相连端）；无拓扑端点时为 null。 */
        firstSide: 'start' | 'end' | null;
        /** 链尾实体的自由端侧；无拓扑端点时为 null。 */
        lastSide: 'start' | 'end' | null;
        /** 链首自由端点对象。 */
        firstFree: SketchPointObject | null;
        /** 链尾自由端点对象。 */
        lastFree: SketchPointObject | null;
    } {
        if (chain.length === 0) {
            return { firstSide: null, lastSide: null, firstFree: null, lastFree: null };
        }

        const first = chain[0]!;
        const last = chain[chain.length - 1]!;
        const firstEndpoints = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(first);
        const lastEndpoints = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(last);

        if (chain.length === 1) {
            // 单实体链：无相邻实体，start/end 两端均为自由端
            return {
                firstSide: firstEndpoints.start ? 'start' : null,
                lastSide: firstEndpoints.end ? 'end' : null,
                firstFree: firstEndpoints.start,
                lastFree: firstEndpoints.end,
            };
        }

        // 链首：与 chain[1] 共享的端为相连端，另一端为自由端
        const second = chain[1]!;
        const firstShared = CombineSketchEntitiesUnifiedDirection.getSharedEndpointObject(first, second);
        const firstSide: 'start' | 'end' | null = firstShared
            ? firstEndpoints.start?.uuid === firstShared.uuid ? 'end' : 'start'
            : null;

        // 链尾：与 chain[last-1] 共享的端为相连端，另一端为自由端
        const prev = chain[chain.length - 2]!;
        const lastShared = CombineSketchEntitiesUnifiedDirection.getSharedEndpointObject(last, prev);
        const lastSide: 'start' | 'end' | null = lastShared
            ? lastEndpoints.start?.uuid === lastShared.uuid ? 'end' : 'start'
            : null;

        return {
            firstSide,
            lastSide,
            firstFree: firstSide === 'start'
                ? firstEndpoints.start
                : firstSide === 'end' ? firstEndpoints.end : null,
            lastFree: lastSide === 'start'
                ? lastEndpoints.start
                : lastSide === 'end' ? lastEndpoints.end : null,
        };
    }

    /**
     * 按端点侧取实体的端点对象。
     *
     * 说明：供等距实体顶端加盖在偏移链上按源链推导的自由端侧取端点——
     * 延伸/相交处理会原位替换偏移实体实例且不保证相邻实体共享端点对象，
     * 无法用对象身份反推自由端，只能依赖「偏移链与源链同序同拓扑」的侧一致性。
     *
     * @param entity 草图实体。
     * @param side 端点侧（start / end）。
     * @returns 对应端点对象；无该侧端点或 side 为 null 时返回 null。
     */
    public static getEntityEndpointBySide(
        entity: OffsetCandidate,
        side: 'start' | 'end' | null,
    ): SketchPointObject | null {
        if (!side) {
            return null;
        }
        const endpoints = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(entity);
        return side === 'start' ? endpoints.start : endpoints.end;
    }

    /**
     * 获取实体在指定端点侧的切向（世界坐标单位向量，方向沿实体自身 start→end 的采样走向）。
     *
     * 说明：
     * 1. 直线：切向即 start→end 走向（start 侧与 end 侧同向）；
     * 2. 圆弧/椭圆弧：按参数方程在端点参数角处求导得到局部 2D 切向
     *    （顺时针采样时取反；椭圆弧还需叠加 rotation），再经参考面 x/y 轴映射回世界；
     * 3. 样条/样式样条/抛物线：无解析切向定义时用曲线采样点差分近似
     *    （start 侧取前两点、end 侧取末两点）；
     * 4. 无法获取时返回 null，调用方回退端点弦方向。
     *
     * 公开原因：等距实体顶端加盖的胶囊外侧方向必须在源实体自由端处取切向延伸，
     * 不能用「自由端→相连端」弦向量近似——圆弧/椭圆弧端点处弦向与切向偏差大，
     * 半圆盖鼓包会朝内凹而非胶囊形外凸。
     *
     * @param entity 草图实体。
     * @param side 端点侧（start / end）。
     * @param refPlane 参考面。
     * @returns 世界坐标切向单位向量；无法计算时返回 null。
     */
    public static getEntityEndpointTangentWorld(
        entity: OffsetCandidate,
        side: 'start' | 'end',
        refPlane: ReferencePlane,
    ): Vector3 | null {
        // 直线：切向即 start→end 走向
        if (entity instanceof SketchLineObject) {
            const direction = entity.endPoint.getWorldPosition(new Vector3())
                .sub(entity.startPoint.getWorldPosition(new Vector3()));
            return CombineSketchEntitiesUnifiedDirection.normalizeDirectionOrNull(direction);
        }

        // 圆弧：参数方程 (cosθ, sinθ) 求导，顺时针采样时取反
        if (entity instanceof SketchArcObject) {
            const definition = entity.getArcDefinition();
            const angle = side === 'start' ? definition.startAngle : definition.endAngle;
            const samplingSign = definition.clockwise ? -1 : 1;
            const localTangent = new Vector2(-Math.sin(angle), Math.cos(angle)).multiplyScalar(samplingSign);
            return CombineSketchEntitiesUnifiedDirection.localDirectionToWorld(localTangent, refPlane);
        }

        // 椭圆弧：未旋转系参数方程 (rx·cosθ, ry·sinθ) 求导，顺时针采样取反，
        // 再绕中心旋转 rotation 得到局部 2D 切向
        if (entity instanceof SketchEllipseObject) {
            const definition = entity.getEllipseDefinition();
            const angle = side === 'start' ? definition.startAngle : definition.endAngle;
            const samplingSign = definition.clockwise ? -1 : 1;
            const localTangent = new Vector2(
                -definition.radiusX * Math.sin(angle),
                definition.radiusY * Math.cos(angle),
            )
                .multiplyScalar(samplingSign)
                .rotateAround(new Vector2(0, 0), definition.rotation);
            return CombineSketchEntitiesUnifiedDirection.localDirectionToWorld(localTangent, refPlane);
        }

        // 样条/样式样条/抛物线：用曲线采样点差分近似端点切向
        if (entity instanceof SketchSplineObject
            || entity instanceof SketchStyleSplineObject
            || entity instanceof SketchParabolaObject) {
            const points = entity.getCurvePoints();
            if (points.length < 2) {
                return null;
            }
            const direction = side === 'start'
                ? points[1]!.clone().sub(points[0]!)
                : points[points.length - 1]!.clone().sub(points[points.length - 2]!);
            return CombineSketchEntitiesUnifiedDirection.normalizeDirectionOrNull(direction);
        }

        // 圆/复合实体等无端点切向定义，交由调用方回退弦方向
        return null;
    }

    /**
     * 归一化世界方向向量；退化（零长度）时返回 null。
     *
     * @param direction 世界方向向量（可能退化）。
     * @returns 单位方向向量；退化时返回 null。
     */
    private static normalizeDirectionOrNull(direction: Vector3): Vector3 | null {
        if (direction.lengthSq() < 1e-24) {
            return null;
        }
        return direction.normalize();
    }

    /**
     * 将参考面局部 2D 方向映射为世界方向单位向量；退化时返回 null。
     *
     * @param localDir 局部 2D 方向（可能退化）。
     * @param refPlane 参考面。
     * @returns 世界方向单位向量；退化时返回 null。
     */
    private static localDirectionToWorld(localDir: Vector2, refPlane: ReferencePlane): Vector3 | null {
        if (localDir.lengthSq() < 1e-24) {
            return null;
        }
        const xAxis = refPlane.xAxis;
        const yAxis = refPlane.yAxis;
        return xAxis.multiplyScalar(localDir.x).addScaledVector(yAxis, localDir.y).normalize();
    }

    /**
     * 统计两个实体共享的不同端点数量。
     * 
     * 说明：用于双实体链的闭合判断——开口链共享 1 个端点，闭合链共享 2 个端点。
     * 
     * @param a 实体 A。
     * @param b 实体 B。
     * @returns 共享端点数量（0、1 或 2）。
     */
    private static countSharedEndpoints(a: OffsetCandidate, b: OffsetCandidate): number {
        if (!(a instanceof AbstractSketchEdgeObject) || !(b instanceof AbstractSketchEdgeObject)) {
            return 0;
        }
        const aEndpoints = a.getRelatedSketchPointsSnapshot().filter(p => p.isEndPoint);
        const bEndpoints = b.getRelatedSketchPointsSnapshot().filter(p => p.isEndPoint);
        const sharedUuids = new Set<string>();
        for (const ap of aEndpoints) {
            for (const bp of bEndpoints) {
                if (ap.uuid === bp.uuid) {
                    sharedUuids.add(ap.uuid);
                }
            }
        }
        return sharedUuids.size;
    }

    /**
     * 获取实体当前几何的真实采样点序列（世界坐标）。
     * 
     * 说明：
     * 1. 圆弧/椭圆弧/抛物线/圆/直线直接读取 geometry.attributes.position
     *    （每 3 个数字一个 Vector3，见 {@link readGeometryPositionPoints}），
     *    该数据由各对象 updateGeometry 维护，与当前渲染几何完全一致，
     *    避免调用方自行插值或重新采样产生偏差；
     * 2. 样条/样式样条复用公共 API getCurvePoints()（与 geometry 同源），
     *    保持其既有语义数据行为；
     * 3. 采样序列的首末点即实体端点（弧的 getPoints 含两端点），
     *    供闭合链轮廓按链拓扑拼接时对齐方向。
     * 
     * @param entity 草图实体。
     * @returns 世界坐标采样点数组；无法获取时返回空数组。
     */
    private static collectEntitySampledPoints(entity: OffsetCandidate): Vector3[] {
        if (entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
            return entity.getCurvePoints();
        }
        if (entity instanceof AbstractSketchEdgeObject) {
            return readGeometryPositionPoints(entity.geometry);
        }
        return [];
    }

    /**
     * 将世界坐标点投影为参考面局部 2D 坐标（轮廓顶点使用）。
     * 
     * @param point 世界坐标点。
     * @param refPlane 参考面。
     * @returns 2D 局部坐标。
     */
    private static projectToLocal2D(point: Vector3, refPlane: ReferencePlane): Vector2 {
        const local = refPlane.worldToLocal(point);
        return new Vector2(local.x, local.y);
    }

    /**
     * 闭合去重：末点与首点重合时移除最后一个顶点（多边形首尾不重复）。
     * 
     * @param vertices 2D 多边形顶点数组（原地修改）。
     */
    private static deduplicateClosedVertices(vertices: Vector2[]): void {
        if (vertices.length > 1) {
            const first = vertices[0]!;
            const last = vertices[vertices.length - 1]!;
            if (first.distanceToSquared(last) < 1e-12) {
                vertices.pop();
            }
        }
    }

    /**
     * 从闭合选择链构建判定轮廓 —— 按拓扑端点行走拼接各实体的真实采样序列，投影为 2D 多边形。
     * 
     * 设计说明：
     * 1. 轮廓不再使用"共享端点弦近似"：弧/椭圆弧/抛物线等曲线只用端点会丢失弓形区域，
     *    导致射线法内外判定在弓形歧义区出错；改为直接拼接各实体 geometry 的真实采样点，
     *    轮廓与真实边界一致（采样弦高远小于测试点偏移量，判定无歧义）；
     * 2. 多实体闭合链行走规则（基于拓扑端点对象 uuid，而非采样坐标匹配）：
     *    - 起点 = chain[0] 的自由端点（与 chain[n-1] 不共享的端点对象），显式加入轮廓；
     *    - 逐实体（chain[1..n-1]）判断当前点与该实体的相连端点：采样序列始终按
     *      startPoint→endPoint 排列，相连端点为 startPoint 则正向追加，
     *      为 endPoint 则首尾颠倒后追加，且跳过与当前点重合的首点（不重复加入轮廓）；
     *    - 最后将 chain[0] 作为闭合段收尾，从当前行走位置走回起点，补全真实闭合边
     *      （chain[0] 为曲线时避免闭合边被直线近似而丢失弓形区域）；
     * 3. 单实体闭合链（圆/满椭圆/闭合样条）无相邻实体，直接追加全部采样点并闭合去重；
     * 4. 采样失败（空序列）的实体直接跳过；拓扑断链（当前点与实体两端点 uuid 均不匹配）
     *    时保持原始方向兜底并输出日志，不抛异常。
     * 
     * @param chain 已排序的闭合选择链（length ≥ 1，闭合性由调用方 isChainClosed 保证）。
     * @param refPlane 当前激活草图组的参考面（用于世界坐标 ↔ 局部坐标投影）。
     * @returns 2D 多边形顶点数组（Vector2，首尾不重复）。
     */
    private static buildClosedContourPolygon(chain: SketchEntityChain, refPlane: ReferencePlane): Vector2[] {
        const vertices: Vector2[] = [];
        const n = chain.length;
        if (n === 0) return vertices;

        // 1. 收集每个实体的真实采样点序列（世界坐标）
        const sampledList = chain.map((entity) =>
            CombineSketchEntitiesUnifiedDirection.collectEntitySampledPoints(entity),
        );

        // 2. 单实体闭合链（圆/满椭圆/闭合样条）：无相邻实体拓扑，直接追加全部采样点
        if (n === 1) {
            for (const point of sampledList[0]) {
                vertices.push(CombineSketchEntitiesUnifiedDirection.projectToLocal2D(point, refPlane));
            }
            CombineSketchEntitiesUnifiedDirection.deduplicateClosedVertices(vertices);
            return vertices;
        }

        // 3. 多实体闭合链：按拓扑端点行走拼接轮廓
        //    行走起点 = chain[0] 的自由端点（与 chain[n-1] 不共享的端点对象）；
        //    闭合性由调用方 isChainClosed 保证，sharedWithLast 正常情况下非 null。
        const sharedWithLast = CombineSketchEntitiesUnifiedDirection.getSharedEndpointObject(chain[0], chain[n - 1]);
        let walkPoint: SketchPointObject | null = sharedWithLast
            ? CombineSketchEntitiesUnifiedDirection.getOtherEndpointObject(chain[0], sharedWithLast)
            : null;

        // ① 起点显式加入轮廓
        if (walkPoint) {
            vertices.push(CombineSketchEntitiesUnifiedDirection.projectToLocal2D(
                walkPoint.getWorldPosition(new Vector3()), refPlane,
            ));
        }

        // ② 逐实体行走（chain[0] 留到收尾处理，作为闭合段）
        for (let i = 1; i < n; i++) {
            const points = sampledList[i];
            if (points.length === 0) {
                continue; // 采样失败：跳过该实体，连续性由其余实体兜底
            }

            const { start, end } = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(chain[i]);
            let sequence = points;
            let startIndex = 0;
            let nextPoint: SketchPointObject | null = null;

            if (walkPoint) {
                if (start && start.uuid === walkPoint.uuid) {
                    // 相连端点 = startPoint → 采样序列正向，跳过与当前点重合的首点
                    startIndex = 1;
                    nextPoint = end;
                } else if (end && end.uuid === walkPoint.uuid) {
                    // 相连端点 = endPoint → 采样序列按 startPoint→endPoint 排列，需首尾颠倒；跳过首点
                    sequence = [...points].reverse();
                    startIndex = 1;
                    nextPoint = start;
                } else {
                    // 拓扑异常兜底：当前点与该实体两端点 uuid 均不匹配（链断裂），
                    // 保持原始方向继续并放弃拓扑匹配，保证不崩溃
                    console.warn(
                        `[CombineSketchEntitiesUnifiedDirection] 闭合链行走断链：实体 ${chain[i]!.uuid} 与当前行走位置不匹配`,
                    );
                }
            }

            for (let j = startIndex; j < sequence.length; j++) {
                vertices.push(CombineSketchEntitiesUnifiedDirection.projectToLocal2D(sequence[j]!, refPlane));
            }

            walkPoint = nextPoint;
        }

        // ③ 收尾：chain[0] 作为闭合段，从当前行走位置（= chain[0] 与 chain[n-1] 的共享端点）走回起点
        const firstPoints = sampledList[0];
        if (firstPoints.length > 0) {
            const { start: firstStart, end: firstEnd } = CombineSketchEntitiesUnifiedDirection.getEntityEndpointObjects(chain[0]);
            let sequence0 = firstPoints;
            let startIndex0 = 0;
            if (walkPoint) {
                if (firstStart && firstStart.uuid === walkPoint.uuid) {
                    // 当前行走位置 = chain[0] 的 startPoint → 正向走回起点
                    startIndex0 = 1;
                } else if (firstEnd && firstEnd.uuid === walkPoint.uuid) {
                    // 当前行走位置 = chain[0] 的 endPoint → 首尾颠倒后走回起点
                    sequence0 = [...firstPoints].reverse();
                    startIndex0 = 1;
                }
                // 匹配失败（前面已断链）：保持原始方向兜底
            }
            for (let j = startIndex0; j < sequence0.length; j++) {
                vertices.push(CombineSketchEntitiesUnifiedDirection.projectToLocal2D(sequence0[j]!, refPlane));
            }
        }

        // ④ 闭合去重：末点与首点重合时移除最后一个顶点（多边形首尾不重复）
        CombineSketchEntitiesUnifiedDirection.deduplicateClosedVertices(vertices);

        return vertices;
    }

    /**
     * 射线法判断 2D 点是否在多边形内部。
     * 
     * 说明：从测试点向 +X 方向发出一条射线，统计与多边形边的交点数量。
     * 奇数个交点在内部，偶数个在外部。这是标准的 Point-in-Polygon 算法。
     * 
     * @param point 待测试的 2D 点。
     * @param polygon 2D 多边形顶点数组（已闭合，首尾不一定重复也可以）。
     * @returns 点在多边形内部返回 true。
     */
    private static isPointInsidePolygon(point: Vector2, polygon: Vector2[]): boolean {
        const n = polygon.length;
        if (n < 3) return false;

        let inside = false;
        const px = point.x;
        const py = point.y;

        for (let i = 0, j = n - 1; i < n; j = i++) {
            const xi = polygon[i]!.x;
            const yi = polygon[i]!.y;
            const xj = polygon[j]!.x;
            const yj = polygon[j]!.y;

            // 判断边 (j→i) 是否穿过从 point 向右发出的水平射线
            if ((yi > py) !== (yj > py)) {
                const intersectX = (xj - xi) * (py - yi) / (yj - yi) + xi;
                if (px < intersectX) {
                    inside = !inside;
                }
            }
        }

        return inside;
    }

    // ═══════════════════════════════════════════════════════════
    // 闭合链：射线法逐实体判定
    // ═══════════════════════════════════════════════════════════

    /**
     * 计算方向向量的顺时针法向（cw 法向，世界坐标）。
     * 
     * 说明：
     * 1. three.js 右手坐标系中，从 planeNormal 所指方向观察，
     *    cross(dir, planeNormal) 是 dir 顺时针旋转 90° 的方向；
     * 2. 该方向是本类统一的"默认偏移侧"基准（isReverse=false），
     *    与开口链行进方向修正（{@link applyChainDirectionCorrection}）的
     *    默认偏移侧假设完全一致：直线取 start→end、曲线取弦方向/切向量的 cw 法向；
     * 3. 方向向量退化为零（首尾点重合/切向量为零）时返回 null，
     *    由调用方决定回退策略（弧类回退背离圆心/焦点，其余回退全局 isReverse）。
     * 
     * @param dir 方向向量（无需预先归一化）。
     * @param planeNormal 参考面法向量（已归一化）。
     * @returns 单位 cw 法向向量；dir 退化为零向量时返回 null。
     */
    private static computeCwOutward(dir: Vector3, planeNormal: Vector3): Vector3 | null {
        // 首尾重合/切向量为零（退化）时无法确定法向，交由调用方回退
        if (dir.lengthSq() < 1e-10) {
            return null;
        }
        return new Vector3().crossVectors(dir.clone().normalize(), planeNormal).normalize();
    }

    /**
     * 获取实体代表性中点及其默认外侧法向量（世界坐标）。
     * 
     * 说明：
     * 1. 中点用于在偏移后生成射线法测试点；
     * 2. 外侧法向量（isReverse=false 基准）统一为实体起点→终点方向的
     *    顺时针法向（cw 法向，见 {@link computeCwOutward}），与开口链
     *    行进方向修正（{@link applyChainDirectionCorrection}）的默认偏移侧
     *    语义完全一致；圆弧/椭圆弧/抛物线首尾点重合（如满椭圆）时回退
     *    "背离圆心/焦点"方向；
     * 3. 对于曲线实体（圆弧/椭圆弧/抛物线），取 geometry 真实采样序列的
     *    中间点，中点落在真实曲线上，避免"弦中点 + 弓形矢高"导致测试点
     *    深陷轮廓内部或半圆弧"弦中点=圆心"时 outward 退化为零向量；
     * 4. 对于直线，取几何中点及 dir×n 方向；
     * 5. 对于样条，取采样点序列中间点及该点切向量的 cw 法向；
     * 6. 注意：cw 法向对优弧（>180°）在弧中点处可能指向轮廓内侧，此时
     *    测试点落入多边形内部，由射线法判定翻转（baseline=true）兜底，
     *    最终偏移方向仍正确朝轮廓同侧。
     * 
     * @param entity 草图实体。
     * @param planeNormal 参考面法向量（已归一化）。
     * @param refPlane 当前激活草图组的参考面（圆取右侧象限点需局部坐标）。
     * @returns { midPoint, outward } 或 null。
     */
    private static getEntityMidPointAndOutwardNormal(
        entity: OffsetCandidate,
        planeNormal: Vector3,
        refPlane: ReferencePlane,
    ): { midPoint: Vector3; outward: Vector3 } | null {
        const entityType = entity.objectType;

        // —— 直线：几何中点 + start→end 的 cw 法向 ——
        if (entity instanceof SketchLineObject) {
            const start = entity.startPoint.position;
            const end = entity.endPoint.position;
            const mid = start.clone().add(end).multiplyScalar(0.5);
            const outward = CombineSketchEntitiesUnifiedDirection.computeCwOutward(
                end.clone().sub(start), planeNormal,
            );
            if (!outward) return null; // 零长度直线（退化）→ 上层回退全局 isReverse
            return { midPoint: mid, outward };
        }

        // —— 圆弧 / 椭圆弧 / 抛物线：采样序列中点 + start→end 的 cw 法向 ——
        if (entity instanceof SketchArcObject) {
            const points = CombineSketchEntitiesUnifiedDirection.collectEntitySampledPoints(entity);
            if (points.length === 0) return null;
            const mid = points[Math.floor(points.length / 2)]!.clone();
            let outward = CombineSketchEntitiesUnifiedDirection.computeCwOutward(
                entity.getEndPoint().getPosition().sub(entity.getStartPoint().getPosition()),
                planeNormal,
            );
            if (!outward) {
                // 退化回退（首尾点重合）：背离圆心方向
                const center = entity.getArcDefinition().centerWorld;
                outward = mid.clone().sub(center).normalize();
            }
            return { midPoint: mid, outward };
        }

        if (entity instanceof SketchEllipseObject) {
            const points = CombineSketchEntitiesUnifiedDirection.collectEntitySampledPoints(entity);
            if (points.length === 0) return null;
            const mid = points[Math.floor(points.length / 2)]!.clone();
            let outward = CombineSketchEntitiesUnifiedDirection.computeCwOutward(
                entity.getEndPoint().getPosition().sub(entity.getStartPoint().getPosition()),
                planeNormal,
            );
            if (!outward) {
                // 退化回退（满椭圆首尾点重合）：背离圆心方向
                const center = entity.getEllipseDefinition().centerWorld;
                outward = mid.clone().sub(center).normalize();
            }
            return { midPoint: mid, outward };
        }

        if (entity instanceof SketchParabolaObject) {
            const points = CombineSketchEntitiesUnifiedDirection.collectEntitySampledPoints(entity);
            if (points.length === 0) return null;
            const mid = points[Math.floor(points.length / 2)]!.clone();
            let outward = CombineSketchEntitiesUnifiedDirection.computeCwOutward(
                entity.getEndPoint().getPosition().sub(entity.getStartPoint().getPosition()),
                planeNormal,
            );
            if (!outward) {
                // 退化回退（首尾点重合）：采样中点从焦点向外
                const def = entity.getParabolaDefinition();
                outward = mid.clone().sub(def.focalWorld).normalize();
            }
            return { midPoint: mid, outward };
        }

        // —— 圆：取右侧象限点 ——
        if (entityType === 'Circle') {
            const circle = entity as SketchCircleObject;
            const center = circle.vec3dCenter;
            // 在参考面局部坐标中取 center + (radius, 0)
            const localCenter = refPlane.worldToLocal(center);
            const localMid = new Vector2(localCenter.x + circle.radius, localCenter.y);
            const mid = refPlane.localToWorld(localMid);
            const outward = mid.clone().sub(center).normalize();
            return { midPoint: mid, outward };
        }

        // —— 样条：采样点中间位置 + 局部切向量 cw 法向 ——
        if (entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
            const curvePoints = entity.getCurvePoints?.() ?? [];
            if (curvePoints.length < 3) return null; // 样条至少需要 3 个采样点
            const midIdx = Math.floor(curvePoints.length / 2);
            const midPt = curvePoints[midIdx]!.clone();
            // 用前后两点近似切向量
            const prevIdx = Math.max(0, midIdx - 1);
            const nextIdx = Math.min(curvePoints.length - 1, midIdx + 1);
            const tangent = curvePoints[nextIdx]!.clone().sub(curvePoints[prevIdx]!).normalize();
            const outward = CombineSketchEntitiesUnifiedDirection.computeCwOutward(tangent, planeNormal);
            if (!outward) return null; // 切向量退化（采样点重合）→ 上层回退全局 isReverse
            return { midPoint: midPt, outward };
        }

        return null;
    }

    /**
     * 为闭合选择链中每个实体计算有效偏移方向（逐实体 isReverse）。
     * 
     * 说明：使用射线法（Point-in-Polygon）逐实体独立判定。
     * 1. 按链拓扑拼接各实体 geometry 的真实采样点构建 2D 多边形轮廓
     *    （曲线不再用端点弦近似，两实体闭合链也不会退化为不足 3 顶点）；
     * 2. 对每个实体取其代表性中点和默认外侧法向量；
     * 3. 沿外侧方向偏移微量得到测试点，用射线法判断测试点是否在多边形内部；
     * 4. 在内 → baseline = true（翻转）；在外 → baseline = false；
     * 5. 最终 isReverse = baseline XOR globalIsReverse。
     * 
     * @param chain 已排序的闭合选择链。
     * @param planeNormal 参考面法向量（已归一化）。
     * @param refPlane 当前激活草图组的参考面。
     * @param globalIsReverse 全局反向开关（等距实体属性面板的"反向"选项）。
     * @returns entity uuid → final isReverse 的映射。
     */
    private static computeClosedChainEffectiveReverse(
        chain: SketchEntityChain,
        planeNormal: Vector3,
        refPlane: ReferencePlane,
        globalIsReverse: boolean,
    ): Map<string, boolean> {
        const result = new Map<string, boolean>();

        // 构建 2D 多边形为射线法判定提供轮廓
        const polygon = CombineSketchEntitiesUnifiedDirection.buildClosedContourPolygon(chain, refPlane);
        if (polygon.length < 3) {
            // 不能构成多边形（退化），统一回退到全局 isReverse
            for (const e of chain) {
                result.set(e.uuid, globalIsReverse);
            }
            return result;
        }

        // 逐实体判定
        for (const entity of chain) {
            const midInfo = CombineSketchEntitiesUnifiedDirection.getEntityMidPointAndOutwardNormal(entity, planeNormal, refPlane);
            if (!midInfo) {
                // 无法获取中点/法向 → 回退到全局 isReverse
                result.set(entity.uuid, globalIsReverse);
                continue;
            }

            // 沿默认外侧方向偏移微量（1mm），投影到 2D 坐标
            const testPoint3D = midInfo.midPoint.clone().addScaledVector(midInfo.outward, 0.001);
            const testLocal = refPlane.worldToLocal(testPoint3D);
            const test2D = new Vector2(testLocal.x, testLocal.y);

            // 射线法判断测试点是否在轮廓内部
            const inside = CombineSketchEntitiesUnifiedDirection.isPointInsidePolygon(test2D, polygon);

            // 在内 → 默认外侧法向量实际指向内侧 → baseline = true（翻转）
            const baselineIsReverse = inside;
            result.set(entity.uuid, baselineIsReverse !== globalIsReverse);
        }

        return result;
    }

    // ═══════════════════════════════════════════════════════════
    // 开口链：统一行进方向修正
    // ═══════════════════════════════════════════════════════════

    /**
     * 为选择链中每个实体计算最终的有效偏移方向（逐实体 isReverse）。
     * 
     * 说明：组合草图实体只分闭合与开口两种情况。
     * 1. 先判断链是否为闭合链；
     * 2. 闭合链 → 射线法逐实体独立判定（{@link computeClosedChainEffectiveReverse}），不修改；
     * 3. 开口链 → 统一使用行进方向修正（{@link applyChainDirectionCorrection}）：
     *    - 以各实体自身 start→end 方向的默认偏移侧（全局 isReverse）为 baseline；
     *    - 用链连接拓扑确定的统一行进方向与各实体自身方向比较，反向则翻转；
     *    - 无论链中是否含弧（圆弧/椭圆弧/抛物线均有首尾端点），均适用该算法；
     * 4. 最终 isReverse = baseline XOR globalIsReverse。
     * 
     * @param chain 已排序的选择链。
     * @param refPlane 当前激活草图组的参考面。
     * @param globalIsReverse 全局反向开关（等距实体属性面板的"反向"选项）。
     * @returns entity uuid → final isReverse 的映射。
     */
    static computeChainEffectiveReverseFlags(
        chain: SketchEntityChain,
        refPlane: ReferencePlane,
        globalIsReverse: boolean,
    ): Map<string, boolean> {
        // 参考面法向量（世界坐标系，归一化），用于闭合链射线法判定
        const planeNormal = refPlane.normal.clone().normalize();

        // 闭合链 → 射线法逐实体独立判定（全部朝轮廓同侧偏移）
        if (CombineSketchEntitiesUnifiedDirection.isChainClosed(chain)) {
            return CombineSketchEntitiesUnifiedDirection.computeClosedChainEffectiveReverse(
                chain, planeNormal, refPlane, globalIsReverse,
            );
        }

        // 开口链 → 统一使用行进方向修正：
        // 1. 先构建 baseline 映射：所有实体以"自身 start→end 方向的默认偏移侧"为准（全局 isReverse）；
        // 2. 再交由 applyChainDirectionCorrection 用链连接拓扑的统一行进方向修正各段偏移侧。
        const baselineMap = new Map<string, boolean>();
        for (const entity of chain) {
            baselineMap.set(entity.uuid, globalIsReverse);
        }
        return CombineSketchEntitiesUnifiedDirection.applyChainDirectionCorrection(chain, baselineMap, refPlane);
    }

    /**
     * 对开口选择链，根据链几何行进方向修正每条实体的 isReverse。
     * 
     * 算法说明：
     * 1. 链已经过 SketchEntitiesSort 排序，chain[0] 是叶子实体（链的一端）；
     * 2. chain[0] 的链方向 = 自由端点（不与 chain[1] 共享的端点）→ 与 chain[1] 的共享端点；
     * 3. chain[i]（i > 0）的链方向 = 与 chain[i-1] 的共享端点 → 另一个端点；
     * 4. 对每条实体，比较链方向与实体自身方向（直线用 endPoint-startPoint，
     *    圆弧/椭圆弧/抛物线用首尾端点连线，样条用采样点末点-首点），
     *    若反向（点积 < 0），翻转该实体的 isReverse。
     * 5. 抛物线例外：其默认偏移侧由射线法决定（{@link SketchEntityEquidistantOffset.resolveParabolaDefaultIsOutward}），
     *    可能落在「弦顺时针法向」的异侧，不能套用第 4 条的弦方向比较——
     *    改为直接比较「链统一侧」与「抛物线默认偏移侧在相连端的实际法向」，反向才翻转。
     * 
     * 意义：消除因各实体绘制方向（startPoint→endPoint）不一致导致的偏移方向参差不齐问题，
     * 用链连接拓扑确定的统一行进方向替代实体自身方向判定偏移侧。
     * 
     * @param chain 已排序的开口选择链。
     * @param effectiveReverseMap 由调用方产出的 entity uuid → isReverse 映射（baseline）。
     * @param refPlane 草图参考面（抛物线解析切向与偏移侧判定需要）。
     * @returns 修正后的 entity uuid → isReverse 映射（新 Map，不修改入参）。
     */
    private static applyChainDirectionCorrection(
        chain: SketchEntityChain,
        effectiveReverseMap: Map<string, boolean>,
        refPlane: ReferencePlane,
    ): Map<string, boolean> {
        const corrected = new Map(effectiveReverseMap); // 复制，不修改入参
        const n = chain.length;

        // 单实体链无需修正（只有一个实体，没有方向不一致的问题）
        if (n <= 1) return corrected;

        const planeNormal = refPlane.normal.clone().normalize();

        for (let i = 0; i < n; i++) {
            const entity = chain[i];
            let chainDir: Vector3 | null = null;

            // —— 计算该实体在链中的几何行进方向向量 ——
            if (i === 0) {
                // 第一个叶子实体：自由端 → 与 chain[1] 共享端
                const shared = CombineSketchEntitiesUnifiedDirection.getSharedEndpoint(entity, chain[1]);
                if (!shared) continue;
                const free = CombineSketchEntitiesUnifiedDirection.getOtherEndpoint(entity, shared);
                chainDir = shared.clone().sub(free).normalize();
            } else {
                // 非首实体：与 chain[i-1] 共享端 → 另一端
                const shared = CombineSketchEntitiesUnifiedDirection.getSharedEndpoint(entity, chain[i - 1]);
                if (!shared) continue;
                const other = CombineSketchEntitiesUnifiedDirection.getOtherEndpoint(entity, shared);
                chainDir = other.clone().sub(shared).normalize();
            }

            if (!chainDir) continue;

            // —— 抛物线：直接比较链统一侧与抛物线默认偏移侧在相连端的实际法向 ——
            if (entity instanceof SketchParabolaObject) {
                const neighbor = chain[i === 0 ? 1 : i - 1]!;
                const sharedObj = CombineSketchEntitiesUnifiedDirection.getSharedEndpointObject(entity, neighbor);
                // 相连端角色：必须明确命中起点或终点之一（首尾退化重合时两者都命中，保持原方向）
                const jointAtStart = sharedObj !== null && sharedObj.uuid === entity.getStartPoint().uuid;
                const jointAtEnd = sharedObj !== null && sharedObj.uuid === entity.getEndPoint().uuid;
                if (jointAtStart === jointAtEnd) continue;

                const jointDirectionInfo = CombineSketchEntitiesUnifiedDirection.resolveParabolaJointDirectionInfo(
                    entity, refPlane, jointAtStart, planeNormal,
                );
                if (!jointDirectionInfo) continue;

                // 链行进方向退化回退：零长度抛物线等场景下"另一端点"与共享端点重合，
                // getOtherEndpoint 回退返回共享端点导致 chainDir 为零向量——
                // 此时以相连端切向（采样行进方向）作为链行进方向继续判定
                const effectiveChainDir = chainDir.lengthSq() > 1e-24
                    ? chainDir
                    : jointDirectionInfo.traversalTangent;

                // 链统一侧 = 链行进方向的顺时针90°（与线段等其它实体的默认偏移侧基准一致）
                const unifiedSide = new Vector3().crossVectors(effectiveChainDir, planeNormal).normalize();
                if (unifiedSide.dot(jointDirectionInfo.defaultSideNormal) < 0) {
                    const currentIsReverse = corrected.get(entity.uuid) ?? false;
                    corrected.set(entity.uuid, !currentIsReverse);
                }
                continue;
            }

            // —— 计算实体自身方向 ——
            let entityDir: Vector3 | null = null;

            if (entity instanceof SketchLineObject) {
                // 直线：绘制方向 = endPoint - startPoint
                entityDir = entity.endPoint.position.clone()
                    .sub(entity.startPoint.position).normalize();
            } else if (entity instanceof SketchArcObject
                || entity instanceof SketchEllipseObject) {
                // 圆弧 / 椭圆弧：首尾端点连线方向作为"等效方向"。
                // 说明：此类曲线均有明确的首尾端点（arcStartPoint/arcEndPoint、
                // ellipsePointA/ellipsePointB），且默认偏移方向已统一为
                // "起点→终点向量的顺时针法向"，与线段/样条一致，
                // 因此可直接套用与线段相同的行进方向修正算法。
                entityDir = entity.getEndPoint().getPosition().clone()
                    .sub(entity.getStartPoint().getPosition()).normalize();
            } else if (entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject) {
                // 样条：取采样点首末连线方向作为"等效方向"
                const curvePoints = entity.getCurvePoints?.() ?? [];
                if (curvePoints.length >= 2) {
                    const first = curvePoints[0]!;
                    const last = curvePoints[curvePoints.length - 1]!;
                    entityDir = last.clone().sub(first).normalize();
                }
            }
            // 圆无首尾端点（闭合实体），不会出现在开口链中，无需处理

            if (!entityDir || entityDir.lengthSq() < 1e-10) continue;

            // —— 方向比较：反向则翻转 isReverse ——
            const dot = chainDir.dot(entityDir);
            if (dot < 0) {
                // 链方向与实体自身方向相反 → 原始 isReverse 判定基于的方向与链方向矛盾 → 翻转
                const currentIsReverse = corrected.get(entity.uuid) ?? false;
                corrected.set(entity.uuid, !currentIsReverse);
            }
            // dot >= 0 时不翻转，保持原值
        }

        return corrected;
    }

    /**
     * 求解抛物线在链相连端的「默认偏移侧法向」与「采样行进切向」。
     * 
     * 说明：
     * 1. 默认偏移侧由射线法决定（与创建路径共用 {@link SketchEntityEquidistantOffset.resolveParabolaDefaultIsOutward}）：
     *    外侧 = 切向逆时针90°（ccw），内侧 = 切向顺时针90°（cw）；
     * 2. 相连端切向由抛物线定义解析推导（P(t) = apex + side·t + axis·t²/(4f)，
     *    dP/dt = side + axis·t/(2f)），按采样行进方向定号（起点/终点均为
     *    采样行进方向，与等距样条端点法向平移的切向口径一致）；
     * 3. traversalTangent 同时作为链行进方向退化（另一端点与共享端点重合）时的回退方向。
     * 
     * @param entity 抛物线实体。
     * @param refPlane 草图参考面。
     * @param jointAtStart 相连端是否为起点（false 表示终点）。
     * @param planeNormal 参考面法向量（已归一化）。
     * @returns 相连端方向信息；定义退化（焦点与顶点重合）时返回 null。
     */
    private static resolveParabolaJointDirectionInfo(
        entity: SketchParabolaObject,
        refPlane: ReferencePlane,
        jointAtStart: boolean,
        planeNormal: Vector3,
    ): { defaultSideNormal: Vector3; traversalTangent: Vector3 } | null {
        // 默认偏移侧（与创建路径同一射线法口径；弦退化回退外侧）
        const defaultOutward = SketchEntityEquidistantOffset.resolveParabolaDefaultIsOutward(entity, refPlane) ?? true;

        // 解析相连端切向：dP/dt = side + axis·t/(2f)
        const definition = entity.getParabolaDefinition();
        const axisVector = definition.focalWorld.clone().sub(definition.apexWorld);
        axisVector.sub(planeNormal.clone().multiplyScalar(axisVector.dot(planeNormal)));
        const focalLength = axisVector.length();
        if (focalLength < 1e-12) {
            return null;
        }
        const axisDir = axisVector.normalize();
        const sideDir = new Vector3().crossVectors(axisDir, planeNormal).normalize();

        const parameter = jointAtStart ? definition.startParameter : definition.endParameter;
        const tangent = sideDir.clone().addScaledVector(axisDir, parameter / (2 * focalLength));
        if (tangent.lengthSq() < 1e-24) {
            return null;
        }

        // 端点切向按采样行进方向定号（起点/终点一致，与 SplineOffsetGeometryService 同口径）
        const parameterSpan = definition.endParameter - definition.startParameter;
        const samplingSign = parameterSpan >= 0 ? 1 : -1;
        const traversalTangent = tangent.clone().multiplyScalar(samplingSign).normalize();

        // 默认偏移侧：外侧 = 切向逆时针90°（ccw），内侧 = 切向顺时针90°（cw）
        const defaultSideNormal = defaultOutward
            ? new Vector3().crossVectors(planeNormal, traversalTangent).normalize()
            : new Vector3().crossVectors(traversalTangent, planeNormal).normalize();

        return { defaultSideNormal, traversalTangent };
    }
}
